<template>
  <div class="rv">
    <div class="rv-card">
      <!-- Branded header — office logo when set (teamio.sites.LogoUrl), else
           initial avatar + name (same convention as TxPatientView). -->
      <div class="rv-header" v-if="landing">
        <img
          v-if="landing.practiceLogoUrl"
          :src="landing.practiceLogoUrl"
          :alt="landing.practiceName"
          class="rv-logo-img"
        />
        <template v-else>
          <div class="rv-logo">{{ practiceInitial }}</div>
          <div class="rv-practice-name">{{ landing.practiceName }}</div>
        </template>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="rv-state">
        <i class="fa fa-circle-o-notch fa-spin"></i>
        <p>One moment…</p>
      </div>

      <!-- Expired / dead link -->
      <div v-else-if="errorType === 'expired'" class="rv-state">
        <div class="rv-icon-circle rv-icon-muted"><i class="fa fa-clock-o"></i></div>
        <h3>This link is no longer active</h3>
        <p>It may have expired. If you'd like to share feedback, please contact your dental office directly.</p>
      </div>

      <!-- Generic error -->
      <div v-else-if="errorType === 'error'" class="rv-state">
        <div class="rv-icon-circle rv-icon-muted"><i class="fa fa-exclamation-circle"></i></div>
        <h3>Something went wrong</h3>
        <p>We couldn't load this page. Please try again in a moment.</p>
      </div>

      <!-- Already completed — friendly "thanks again" -->
      <div v-else-if="step === 'completed'" class="rv-state">
        <div class="rv-icon-circle"><i class="fa fa-check"></i></div>
        <h3>Thanks again{{ firstName ? ', ' + firstName : '' }}!</h3>
        <p>You've already shared your feedback with us — we really appreciate it.</p>
      </div>

      <!-- STEP: rate -->
      <div v-else-if="step === 'rate' && landing" class="rv-step">
        <h3 class="rv-title">How was your visit?</h3>
        <p class="rv-sub">
          {{ firstName ? 'Thanks for coming in, ' + firstName + '. ' : '' }}How would you rate your experience?
        </p>
        <StarPicker :value="rating" :disabled="submittingRating" @select="onSelectRating" />
        <div v-if="ratingError" class="rv-inline-error">{{ ratingError }}</div>
      </div>

      <!-- STEP: public (happy branch — Google CTA is PROMINENT here) -->
      <div v-else-if="step === 'public' && landing" class="rv-step rv-step-center">
        <div class="rv-icon-circle">🎉</div>
        <div class="rv-stars">{{ starString }}</div>
        <h3 class="rv-title">So glad you enjoyed it!</h3>
        <p class="rv-sub">
          Would you mind sharing that on Google? It takes about 15 seconds and means the world to our team.
        </p>

        <button
          v-if="googleLink"
          type="button"
          class="rv-google-btn"
          @click="openPlatform('google')"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#4285F4" stroke="#4285F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.11a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.897a.53.53 0 0 1 .294-.905l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z" />
          </svg>
          Review us on Google
        </button>

        <!-- Secondary platforms (office-pasted links, shown when enabled + verified). -->
        <div v-if="secondaryPlatforms.length" class="rv-secondary-row">
          <button
            v-for="p in secondaryPlatforms"
            :key="p.platform"
            type="button"
            class="rv-secondary-btn"
            @click="openPlatform(p.platform)"
          >
            Review us on {{ p.label }}
          </button>
        </div>

        <div class="rv-small-link" @click="maybeLater">Maybe later</div>

        <!-- Nudged gate: the private option stays reachable from the happy
             branch too (small link, per the approved plan's addition). -->
        <div class="rv-small-link rv-small-link-alt" @click="step = 'private'">
          Or send private feedback instead
        </div>
      </div>

      <!-- STEP: private (sub-threshold branch — private form is PROMINENT) -->
      <div v-else-if="step === 'private' && landing" class="rv-step">
        <div class="rv-stars rv-stars-sm">{{ starString }}</div>
        <h3 class="rv-title rv-title-sm">We want to make this right</h3>
        <p class="rv-sub rv-sub-sm">
          This goes straight to our office — not online. What let you down?
        </p>

        <PrivateFeedbackForm
          :reasons="landing.reasons"
          :submitting="submittingFeedback"
          :error="feedbackError"
          @submit="onFeedbackSubmit"
        />

        <!-- ⚖️ NUDGED COMPLIANT GATE: the public review option must remain
             available in BOTH branches (Google anti-gating policy — only
             PROMINENCE may differ). Do not remove or conditionally hide this
             link based on the rating. -->
        <div
          v-if="publicOptionLink"
          class="rv-small-link rv-small-link-alt"
          @click="openPlatform(publicOptionLink.platform)"
        >
          Prefer to post publicly? Review us on {{ publicOptionLink.label }}
        </div>
      </div>

      <!-- STEP: done -->
      <div v-else-if="step === 'done'" class="rv-state">
        <div class="rv-icon-circle"><i class="fa fa-check"></i></div>
        <h3>Thank you{{ firstName ? ', ' + firstName : '' }}</h3>
        <p v-if="doneVariant === 'feedback'">
          Your feedback went straight to our office. We'll reach out personally to make it right.
        </p>
        <p v-else-if="doneVariant === 'public'">
          Thanks so much for sharing — it means the world to our team.
        </p>
        <p v-else>
          No problem — thanks for letting us know how we did!
        </p>
      </div>

      <!-- Footer -->
      <div class="rv-footer">Powered by Teamio</div>
    </div>
  </div>
</template>

<script>
/**
 * ReviewLanding — the public patient rating page (/rv/:shortId).
 * Reviews milestone A4; canonical plan: Documents/teamio-reviews.md.
 *
 * Modeled on TxPatientView: standalone (no Layout/Sidebar), loads by the
 * shortId route param through a small store module, mobile-first.
 *
 * State machine: loading → rate → (public | private) → done, plus
 * expired / error / completed side states.
 *
 * ⚖️ NUDGED COMPLIANT GATE (approved decision #1): every rating can reach the
 * public Google option. ≥ gateThreshold sees a prominent Google CTA (with a
 * small private-feedback link); < gateThreshold sees the private form first
 * (with a small public-review link). Availability NEVER flips — only
 * prominence. Enforced server-side too (the API returns all enabled links in
 * both branches).
 */
import { mapState } from 'vuex';
import StarPicker from './components/StarPicker.vue';
import PrivateFeedbackForm from './components/PrivateFeedbackForm.vue';

const PLATFORM_LABELS = { google: 'Google', facebook: 'Facebook', yelp: 'Yelp' };
const OUTCOME_BY_PLATFORM = {
  google: 'google_click',
  facebook: 'facebook_click',
  yelp: 'yelp_click',
};

export default {
  name: 'ReviewLanding',
  components: { StarPicker, PrivateFeedbackForm },

  data() {
    return {
      step: 'rate',
      rating: 0,
      submittingRating: false,
      ratingError: '',
      submittingFeedback: false,
      feedbackError: '',
      doneVariant: 'later', // 'feedback' | 'public' | 'later'
    };
  },

  computed: {
    ...mapState('reviewLanding', {
      loading: (s) => s.loading,
      errorType: (s) => s.errorType,
      landing: (s) => s.landing,
    }),

    firstName() {
      return (this.landing && this.landing.patientFirstName) || '';
    },
    practiceInitial() {
      const n = ((this.landing && this.landing.practiceName) || 'P').trim();
      return (n.charAt(0) || 'P').toUpperCase();
    },
    starString() {
      return '★'.repeat(this.rating || 0);
    },
    reviewLinks() {
      return (this.landing && this.landing.reviewLinks) || {};
    },
    googleLink() {
      return this.reviewLinks.google || null;
    },
    /**
     * Secondary platform buttons for the happy branch: Facebook/Yelp when the
     * office enabled them AND verified the link (verifiedAt) in settings.
     */
    secondaryPlatforms() {
      return ['facebook', 'yelp']
        .filter((p) => this.reviewLinks[p] && this.reviewLinks[p].url && this.reviewLinks[p].verifiedAt)
        .map((p) => ({ platform: p, label: PLATFORM_LABELS[p] }));
    },
    /**
     * The public option offered from the PRIVATE branch (compliance link).
     * Google when configured; otherwise the first available platform so the
     * public path never disappears while any link is enabled.
     */
    publicOptionLink() {
      const order = ['google', 'facebook', 'yelp'];
      const platform = order.find((p) => this.reviewLinks[p] && this.reviewLinks[p].url);
      return platform ? { platform, label: PLATFORM_LABELS[platform] } : null;
    },
  },

  watch: {
    '$route.params.shortId': {
      immediate: true,
      handler(shortId) {
        if (shortId) this.load(shortId);
      },
    },
  },

  methods: {
    async load(shortId) {
      try {
        const data = await this.$store.dispatch('reviewLanding/loadLanding', { shortId });
        if (data && data.alreadyCompleted) {
          this.rating = data.rating || 0;
          this.step = 'completed';
        } else {
          this.step = 'rate';
        }
      } catch (e) {
        /* errorType set in store state */
      }
    },

    async onSelectRating(n) {
      if (this.submittingRating) return;
      this.rating = n;
      this.submittingRating = true;
      this.ratingError = '';
      try {
        const data = await this.$store.dispatch('reviewLanding/submitRating', { rating: n });
        this.step = data.branch === 'public' ? 'public' : 'private';
      } catch (e) {
        // Network hiccup: don't strand the patient — branch client-side from the
        // landing payload's gateThreshold. Compliance holds either way (both
        // branches render the public option from the already-loaded links).
        console.warn('[ReviewLanding] submitRating failed; branching client-side:', e && e.message);
        const gate = (this.landing && this.landing.gateThreshold) || 4;
        this.step = n >= gate ? 'public' : 'private';
      } finally {
        this.submittingRating = false;
      }
    },

    /**
     * Open the office's page on the platform in a new tab, firing the outcome
     * beacon first (fire-and-forget — never blocks or delays the redirect).
     */
    openPlatform(platform) {
      const link = this.reviewLinks[platform];
      if (!link || !link.url) return;
      this.$store.dispatch('reviewLanding/trackOutcome', { outcome: OUTCOME_BY_PLATFORM[platform] });
      window.open(link.url, '_blank', 'noopener');
      this.doneVariant = 'public';
      this.step = 'done';
    },

    maybeLater() {
      this.doneVariant = 'later';
      this.step = 'done';
    },

    async onFeedbackSubmit({ reasons, note }) {
      if (this.submittingFeedback) return;
      this.submittingFeedback = true;
      this.feedbackError = '';
      try {
        await this.$store.dispatch('reviewLanding/submitFeedback', {
          rating: this.rating || undefined,
          reasons,
          note,
        });
        this.doneVariant = 'feedback';
        this.step = 'done';
      } catch (e) {
        this.feedbackError = "We couldn't send that just now — please try again.";
      } finally {
        this.submittingFeedback = false;
      }
    },
  },
};
</script>

<style scoped>
.rv {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 24px 16px 40px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  display: flex;
  justify-content: center;
}
.rv * {
  box-sizing: border-box;
}

/* Mobile-first single card, phone-width even on desktop (per the design). */
.rv-card {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.04);
  padding: 22px 26px 20px;
  display: flex;
  flex-direction: column;
  min-height: 480px;
}

/* Branded header */
.rv-header {
  display: flex;
  align-items: center;
  gap: 9px;
  padding-bottom: 18px;
  border-bottom: 1px solid #f1f5f9;
}
.rv-logo {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #0d5f54;
  color: #ffffff;
  font-weight: 800;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.rv-logo-img {
  max-height: 40px;
  max-width: 220px;
  object-fit: contain;
  display: block;
}
.rv-practice-name {
  font-weight: 800;
  font-size: 14px;
  line-height: 1.2;
}

/* Shared step / state layouts */
.rv-step {
  padding-top: 24px;
  flex: 1;
}
.rv-step-center {
  text-align: center;
}
.rv-state {
  padding-top: 48px;
  text-align: center;
  flex: 1;
  color: #64748b;
  font-size: 13.5px;
  line-height: 1.55;
}
.rv-state .fa-circle-o-notch {
  font-size: 26px;
  color: #94a3b8;
}
.rv-state h3 {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin: 0 0 8px 0;
}
.rv-state p {
  margin: 0 0 8px 0;
}

.rv-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #e6f2f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  font-size: 28px;
}
.rv-icon-circle .fa {
  font-size: 30px;
  color: #0d5f54;
}
.rv-icon-muted {
  background: #f1f5f9;
}
.rv-icon-muted .fa {
  color: #94a3b8;
}

.rv-title {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 8px 0;
  text-align: center;
}
.rv-title-sm {
  font-size: 19px;
}
.rv-sub {
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.55;
  margin: 0 0 26px 0;
  text-align: center;
}
.rv-sub-sm {
  font-size: 13px;
  margin-bottom: 20px;
}

/* Stars shown in the branch headers (the committed rating) */
.rv-stars {
  color: #f59e0b;
  letter-spacing: 3px;
  font-size: 20px;
  margin-bottom: 14px;
  text-align: center;
}
.rv-stars-sm {
  letter-spacing: 2px;
  font-size: 17px;
  margin-bottom: 12px;
}

/* Prominent Google CTA (happy branch) — white button, blue star (design spec) */
.rv-google-btn {
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 13px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition: border-color 0.12s ease;
}
.rv-google-btn:hover {
  border-color: #94a3b8;
}

.rv-secondary-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.rv-secondary-btn {
  flex: 1;
  cursor: pointer;
  font-family: inherit;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  transition: border-color 0.12s ease;
}
.rv-secondary-btn:hover {
  border-color: #cbd5e1;
}

.rv-small-link {
  margin-top: 16px;
  font-size: 12.5px;
  color: #94a3b8;
  cursor: pointer;
  text-align: center;
}
.rv-small-link:hover {
  color: #64748b;
  text-decoration: underline;
}
.rv-small-link-alt {
  margin-top: 12px;
}

.rv-inline-error {
  margin-top: 14px;
  font-size: 12.5px;
  color: #dc2626;
  text-align: center;
}

.rv-footer {
  margin-top: auto;
  padding-top: 20px;
  text-align: center;
  font-size: 10px;
  color: #cbd5e1;
  letter-spacing: 0.04em;
}
</style>
